// Shared streaks. The overview comes from the server; each streak's posts are also kept on this device
// (persisted, never synced: they are other people's posts too), because the cloud keeps them only while the
// streak lives (logic/socialArchive.ts). After that this copy can still be downloaded, but a new browser or
// login does not get it back. The copy belongs to the signed-in account and is dropped when you leave a
// streak, sign out or delete the account. Errors are keys under social.errors.
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid/non-secure';
import i18n from '../i18n';
import * as social from '../platform/social';
import { ApiError } from '../platform/auth';
import { exportText } from '../platform/files';
import { adoptShare, buildShare, parseShare } from '../logic/share';
import { mergeFeed, streakExport } from '../logic/socialArchive';
import { persistStorage } from './storage';
import { useLibrary } from './library';
import type { Recipe, SharedStreak, SocialOverview, SocialPost } from '../types';

type Feed = { posts: SocialPost[]; next: number | null; loading: boolean; streak?: SharedStreak };
type SocialState = {
  /** Whose device copy `feeds` is (an account id); another account starts empty. */
  owner: string | null;
  overview: SocialOverview | null;
  feeds: Record<string, Feed>;
  busy: boolean;
  error: string | null;
  load: () => Promise<void>;
  /** Runs an action that returns the new overview (or anything else), recording a readable error. */
  run: <T>(work: () => Promise<T>) => Promise<T | null>;
  loadFeed: (streakId: string, more?: boolean) => Promise<void>;
  loadAll: () => Promise<void>;
  /** Posts to all of your streaks. */
  post: (caption: string, recipe: Recipe | null, photo: { uri: string; blob?: Blob } | null) => Promise<boolean>;
  saveRecipe: (post: SocialPost) => string | null;
  /** Saves this device's copy of a streak as a file. */
  download: (streakId: string, name: string) => Promise<boolean>;
  /** Sign-out / account deletion: remove this account's streak copies from the device. */
  forget: () => void;
};

export const errorKey = (e: unknown): string => {
  const code = e instanceof ApiError ? e.code : 'generic';
  return i18n.exists(`social.errors.${code}`) ? code : 'generic';
};

export const useSocial = create<SocialState>()(persist((set, get) => ({
  owner: null,
  overview: null,
  feeds: {},
  busy: false,
  error: null,
  load: async () => {
    const overview = await social.overview().catch(e => { set({ error: errorKey(e) }); return null; });
    if (overview) adopt(overview);
  },
  run: async work => {
    set({ busy: true, error: null });
    try {
      const result = await work();
      const r = result as Partial<SocialOverview> | null;
      if (r && typeof r === 'object' && Array.isArray(r.streaks)) adopt(result as unknown as SocialOverview);
      return result;
    } catch (e) {
      set({ error: errorKey(e) });
      return null;
    } finally {
      set({ busy: false });
    }
  },
  loadFeed: async (streakId, more = false) => {
    const current = get().feeds[streakId];
    if (current?.loading || (more && !current?.next)) return;
    set(s => ({ feeds: { ...s.feeds, [streakId]: { posts: current?.posts ?? [], next: current?.next ?? null, loading: true, streak: current?.streak } } }));
    try {
      const page = await social.posts(streakId, more ? current?.next ?? undefined : undefined);
      const local = current?.posts ?? [];
      const posts = more
        ? [...page.posts, ...local.filter(p => !page.posts.some(n => n.id === p.id))].sort((a, b) => b.createdAt - a.createdAt)
        : mergeFeed(local, page.posts, { clearedAt: page.streak?.clearedAt ?? 0, complete: page.next === null });
      set(s => ({ error: null, feeds: { ...s.feeds, [streakId]: { posts, next: page.next, loading: false, streak: page.streak ?? current?.streak } } }));
    } catch (e) {
      set(s => ({ error: errorKey(e), feeds: { ...s.feeds, [streakId]: { ...(s.feeds[streakId] ?? { posts: [], next: null }), loading: false } } }));
    }
  },
  loadAll: async () => {
    await get().load();
    await Promise.all((get().overview?.streaks ?? []).map(s => get().loadFeed(s.id)));
  },
  post: async (caption, recipe, photo) => {
    const lib = useLibrary.getState();
    const share = recipe ? buildShare(recipe, lib) : null;
    const created = await get().run(() => social.createPost(caption, share, !!photo));
    if (!created) return false;
    if (photo) {
      const ok = await get().run(() => social.uploadPhoto(created.id, photo).then(() => true));
      // A post that was meant to carry a photo is not left behind half-made.
      if (!ok) { await social.deletePost(created.id).catch(() => {}); return false; }
    }
    await get().loadAll();
    return true;
  },
  saveRecipe: post => {
    const share = parseShare(post.recipe);
    if (!share) return null;
    const lib = useLibrary.getState();
    const id = nanoid();
    lib.importItems(adoptShare(share, lib, id, post.author.displayName, Date.now()));
    return id;
  },
  download: async (streakId, name) => {
    const posts = get().feeds[streakId]?.posts ?? [];
    const safe = name.replace(/[^\p{L}\p{N} _-]+/gu, '').trim().slice(0, 40) || 'streak';
    return exportText(`chefi-streak-${safe}.json`, streakExport(name, posts, new Date()));
  },
  forget: () => set({ owner: null, overview: null, feeds: {} }),
}), {
  name: 'social',
  storage: persistStorage,
  version: 2, // v1 held buddy conversations; their copies are not streaks
  migrate: () => ({ owner: null, feeds: {} }) as never,
  // Only the device copy of streaks persists; the overview and loading state come from the server.
  partialize: s => ({ owner: s.owner, feeds: Object.fromEntries(Object.entries(s.feeds).map(([id, f]) => [id, { posts: f.posts, next: null, loading: false }])) }) as Partial<SocialState>,
}));

/** A new overview: keep copies only of streaks this account is still in (another account starts empty). */
function adopt(overview: SocialOverview) {
  const { owner, feeds } = useSocial.getState();
  const ids = new Set(overview.streaks.map(s => s.id));
  const own = owner === overview.me ? feeds : {};
  useSocial.setState({ overview, owner: overview.me, error: null, feeds: Object.fromEntries(Object.entries(own).filter(([id]) => ids.has(id))) });
}
